<template>
    <div
        :class="{'isPopup': isPopup}"
        class="media-library">
        <Top
            :filters="filters"
            :active-filter="currentFilter"
            :type-to-pick="typeToPick"
            @newUpload="addUpload"
            @filterChanged="filterChanged"
            @search="val => currentSearch = val"
        />
        <Box
            :box-shadow="!isPopup"
            :padding="!isPopup"
        >
            <div
                v-if="items && shownItems.length > 0"
                class="items flex flex-wrap"
            >
                <div
                    v-for="item in shownItems"
                    :key="item.id"
                    class="column"
                >
                    <MediaItem
                        :item="item"
                        :show-picker="pickItem"
                        :disabled="disabledItems && disabledItems.includes(item.id)"
                        @pickItem="$emit('itemPicked', item)"
                        @updateItem="activeItem = item"
                        @removeItem="dialog = item"
                    />
                </div>
            </div>
            <Spinner v-if="!items" />
            <p v-if="items && shownItems.length < 1">
                No items found
            </p>
        </Box>
        <portal to="mediaPortal">
            <DialogAction
                v-if="dialog"
                heading="Delete item"
                text="Do you really want to delete this item?"
                accept-text="Delete"
                @accept="removeItem(dialog)"
                @close="dialog = null"
            >
                <template v-slot:topIcon>
                    <svg svg-inline="" class="icon" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6"/></svg>
                </template>
            </DialogAction>

            <DialogMediaItem
                v-if="activeItem"
                :item="activeItem"
                heading="Update item"
                @itemUpdated="updateItem"
                @close="activeItem = null"
            >
                <template v-slot:topIcon>
                    <svg svg-inline="" class="icon" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"/><path d="M18 2l4 4-10 10H8v-4L18 2z"/></svg>
                </template>
            </DialogMediaItem>
        </portal>
    </div>
</template>

<script>
import Fuse from 'fuse.js';
import Top from './Top.vue';
import Box from '../ui/Box.vue';
import MediaItem from './MediaItem.vue';
import DialogAction from '../dialog/DialogAction.vue';
import DialogMediaItem from './DialogMediaItem.vue';
import Spinner from '../ui/Spinner.vue';
import { get, remove } from '../../utils/apiHelper';

export default {
    components: {
        Top,
        Box,
        MediaItem,
        DialogAction,
        DialogMediaItem,
        Spinner,
    },
    props: {
        pickItem: {
            type: Boolean,
            default: false,
        },
        isPopup: {
            type: Boolean,
            default: false,
        },
        typeToPick: {
            type: String,
            default: null,
        },
        disabledItems: {
            type: Array,
            default: null,
        },
    },
    data: () => ({
        dialog: null,
        items: null,
        currentFilter: 'all',
        currentSearch: null,
        activeItem: null,
        filters: [
            { label: 'All', value: 'all' },
            { label: 'Images', value: 'image' },
            { label: 'Files', value: 'file' },
            { label: 'Missing alt', value: 'missing_alt' },
        ],
    }),
    computed: {
        shownItems() {
            let { items } = this;

            if (this.currentFilter === 'missing_alt') {
                items = items.filter(item => (!item.alt || item.alt === '') && item.type === 'image');
            } else if (this.currentFilter !== 'all') {
                items = items.filter(item => item.type === this.currentFilter);
            }

            if (this.currentSearch) {
                items = this.searchItems(items);
            }

            return items;
        },
    },
    async created() {
        if (this.typeToPick === 'image') {
            this.currentFilter = 'image';
        }
        const items = await get('/api/media');

        this.items = items.map(item => ({
            title_search: item.title.replace('-', ' '),
            ...item,
        }));
    },
    methods: {
        updateItem(item) {
            const activeItem = this.items.find(i => i.id === item.id);
            const index = this.items.indexOf(activeItem);

            if (index !== -1) {
                this.items[index] = item;
            }
        },
        addUpload(item) {
            this.items.unshift({
                title_search: item.title.replace('-', ' '),
                ...item,
            });
        },
        searchItems(items) {
            const fuse = new Fuse(items, {
                threshold: 0.3,
                shouldSort: true,
                tokenize: true,
                keys: [
                    {
                        name: ['title_search'],
                        weight: 0.8,
                    },
                    {
                        name: ['extension'],
                        weight: 0.2,
                    },
                ],
            });

            return fuse.search(this.currentSearch);
        },
        filterChanged(newFilter) {
            this.currentFilter = newFilter;
        },
        async removeItem(item) {
            this.dialog = null;
            const index = await remove(`/api/media/${item.id}`, this.items, item.id);
            if (index || index === 0) {
                this.items.splice(index, 1);
            }
        },
    },
};
</script>

<style lang="postcss" scoped>
.media-library.isPopup {
    min-height: 100vh;
}
.items {
    margin: -10px;
}
.column {
    width: 12.5%;
    padding: 10px;
    @media (max-width: 2200px) {
        width: 16.6666666667%;
    }
    @media (max-width: 1800px) {
        width: 20%;
    }
    @media (--largeTablet) {
        width: 25%;
    }
}
</style>
